import type { LensQuery } from "@storyboard/event-log/lens";
import type { EventLogQuery } from "@storyboard/event-log/query";
import type { ShapeQuery } from "@storyboard/event-log/shape";
import type { feedItemsLog } from "@storyboard/event-log/specs/feedItems";
import type {
  BootstrapDriverTranscripts,
  BootstrapLatestUnreadEventPerFeed,
  BootstrapWorkspaceAccounts,
  BootstrapWorkspaceCommandAliases,
  BootstrapWorkspaceFeedGroupMemberships,
  BootstrapWorkspaceFeedGroups,
  BootstrapWorkspaceFeedItemEventLog,
  BootstrapWorkspaceFeedItems,
  BootstrapWorkspaceFeedPermissions,
  BootstrapWorkspaceFeedPreferences,
  BootstrapWorkspaceFeeds,
  BootstrapWorkspaceMembershipAvailabilities,
  BootstrapWorkspaceMemberships,
  BootstrapWorkspaceMembershipTags,
  BootstrapWorkspaceTags,
  BootstrapWorkspaceTemplates,
  TranscriptionFormatType,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

/** Cursor-paged window shared by every bootstrap read: `timestamp` is the delta cursor. */
export interface BootstrapPage {
  workspaceId: string;
  page?: number;
  pageSize?: number;
  timestamp?: string;
}

/** The bootstrap routes name the workspace path parameter `id`; web calls the same thing `workspaceId`. */
function bootstrapArgs<P extends { workspaceId: string }>({
  workspaceId,
  ...query
}: P): { id: string } & Omit<P, "workspaceId"> {
  return { id: workspaceId, ...query };
}

export function bootstrapWorkspaceMembershipAvailabilities(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceMembershipAvailabilities> {
  return operations.getBootstrapWorkspacesWorkspaceMembershipAvailabilities(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeedPermissions(
  transport: Transport,
  params: BootstrapPage & { mine?: boolean },
): Promise<BootstrapWorkspaceFeedPermissions> {
  return operations.getBootstrapWorkspacesFeedPermissions(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeeds(transport: Transport, params: BootstrapPage): Promise<BootstrapWorkspaceFeeds> {
  return operations.getBootstrapWorkspacesFeeds(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceNonAliasFeedLatestItems(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceFeedItems> {
  return operations.getBootstrapWorkspacesNonAliasFeedLatestItems(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceAliasFeedLatestOwnerItems(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceFeedItems> {
  return operations.getBootstrapWorkspacesAliasFeedLatestOwnerItems(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceMemberships(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceMemberships> {
  return operations.getBootstrapWorkspacesWorkspaceMemberships(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceTags(transport: Transport, params: BootstrapPage): Promise<BootstrapWorkspaceTags> {
  return operations.getBootstrapWorkspacesTags(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceMembershipTags(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceMembershipTags> {
  return operations.getBootstrapWorkspacesWorkspaceMembershipsTags(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceTemplates(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceTemplates> {
  return operations.getBootstrapWorkspacesTemplates(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceAccounts(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceAccounts> {
  return operations.getBootstrapWorkspacesAccounts(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeedGroups(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceFeedGroups> {
  return operations.getBootstrapWorkspacesFeedGroups(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeedGroupMemberships(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceFeedGroupMemberships> {
  return operations.getBootstrapWorkspacesFeedGroupMemberships(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceCommandAliases(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapWorkspaceCommandAliases> {
  return operations.getBootstrapWorkspacesCommandAliases(transport, bootstrapArgs(params));
}

export function latestUnreadEventPerFeed(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapLatestUnreadEventPerFeed> {
  return operations.getBootstrapWorkspacesLatestUnreadEventPerFeed(transport, bootstrapArgs(params));
}

export function latestUnreadEventPerFeedForOrganizers(
  transport: Transport,
  params: BootstrapPage,
): Promise<BootstrapLatestUnreadEventPerFeed> {
  return operations.getBootstrapWorkspacesLatestUnreadEventPerFeedForOrganizers(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeedPreferences(
  transport: Transport,
  params: BootstrapPage & { mine?: boolean; feedId?: string },
): Promise<BootstrapWorkspaceFeedPreferences> {
  return operations.getBootstrapWorkspacesFeedPreferences(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceFeedItemsPaginated(
  transport: Transport,
  params: BootstrapPage & { feedId?: string; sortAscending?: boolean; itemId?: string; useCreatedAt?: boolean },
): Promise<BootstrapWorkspaceFeedItems> {
  return operations.getBootstrapWorkspacesFeedItems(transport, bootstrapArgs(params));
}

export function bootstrapWorkspaceDriverTranscripts(
  transport: Transport,
  params: BootstrapPage & { language?: string; format?: TranscriptionFormatType },
): Promise<BootstrapDriverTranscripts> {
  return operations.getBootstrapWorkspacesDriverTranscripts(transport, bootstrapArgs(params));
}

/**
 * The item log's query: the shared window parameters, and the lenses and shape parameters `feedItemsLog` declares.
 *
 * `feedIds`, comma-separated, reads those feeds' rows; without it the read is workspace-wide and needs a recent
 * `since`. The log has no shape parameters, so that part of the type is empty.
 */
export type BootstrapEventLogQuery = EventLogQuery &
  LensQuery<typeof feedItemsLog.lenses> &
  ShapeQuery<typeof feedItemsLog.shape> & { workspaceId: string };

/** Reads one page of the feed-item event log: the readable items of the workspace, or of the feeds named. */
export function listFeedItemEventLog(
  transport: Transport,
  query: BootstrapEventLogQuery,
): Promise<BootstrapWorkspaceFeedItemEventLog> {
  return operations.getBootstrapWorkspacesFeedItemsEventLog(transport, bootstrapArgs(query));
}
